/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text, UnstyledButton, planIconFor } from '@/shared/ui';
import { getSpotsStatus, isPlanSelectable, type SpotsStatus } from '../../hooks/use-plans';
import type { BillingCycle, PlanWithFrequency } from '../../hooks/use-plans';
import { PlanCardCta } from './PlanChoice.chrome';
import { planTrialDays } from './Checkout.pricing';
import {
  MARK, TIER_ACCENT, TIER_TILE, formatStorage,
  type RowValue,
  enabledFeatures, featureMatrix, perSeatPrice, resolveTier,
  stripSeparatorDash,
  type QuotaKey, type QuotaLabel, type ResolvedFeatureRow,
} from './PlanChoice.catalog';

export { PlanBillingToggle, PlanChoiceSkeleton } from './PlanChoice.chrome';

const MONTHS_PER_YEAR = 12;

/** Keeps the three audience lines from pushing the price out of alignment. */
const AUDIENCE_MIN_HEIGHT = 32;

/** What the value column prints where a plan does not carry the row. */
const EM_DASH = '—';

/** Colours that switch together when a plan is not on sale. */
interface CardTone {
  accent: string;
  /** Accent on a live plan, muted grey on a waitlisted one. */
  iconAccent: string;
  strong: string;
}

/**
 * Single-path glyph at the card's stroke weight.
 *
 * @param props - Path, pixel size, and stroke colour.
 * @returns An inline SVG icon.
 */
const Glyph: React.FC<{ d: string; size: number; color: string; strokeWidth?: number }> = ({
  d, size, color, strokeWidth = 1.75,
}) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <path d={d} />
  </svg>
);

/**
 * The yes/no mark on a comparison row, in the product's existing artwork.
 *
 * @param props - Whether the plan has the row, and the glyph size.
 * @returns A filled 20×20 check or cross.
 */
const FeatureMark: React.FC<{ on: boolean; size: number }> = ({ on, size }) => (
  <svg
    width={size} height={size} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
    style={{ flex: 'none', marginTop: 2, color: on ? 'var(--plan-feature-on)' : 'var(--plan-feature-off)' }}
  >
    <path fillRule="evenodd" d={on ? MARK.yes : MARK.no} />
  </svg>
);

/**
 * The free days the plan opens with, when the catalogue grants any.
 *
 * It sits under the price rather than in the ribbon over the top edge,
 * which is already spoken for by "recommended" and "chosen" — and losing
 * either of those to say this would trade one fact for another. The card is
 * the first place the offer can be read: before this line existed, a buyer
 * only learned the first week was free one screen later, at the checkout.
 *
 * @param props - The plan and the cycle its card is showing.
 * @returns The line, or null when the plan charges from day zero.
 */
const PlanTrialNote: React.FC<{
  plan: PlanWithFrequency; billingCycle: BillingCycle;
}> = ({ plan, billingCycle }) => {
  const { t } = useTranslation('@pages/onboarding');
  const days = planTrialDays(plan, billingCycle);
  if (days <= 0) return null;
  return (
    <Text
      component="span"
      data-testid={`plan-trial-${plan.key}`}
      style={{
        alignSelf: 'flex-start', padding: '3px var(--space-2-5)',
        borderRadius: 'var(--radius-full)',
        background: 'var(--status-success-bg)', color: 'var(--status-success-text)',
        fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)',
      }}
    >
      {t('planChoice.trial', { count: days })}
    </Text>
  );
};

/**
 * The ribbon over the top edge: why this card, or that it is the chosen one.
 *
 * It replaces the state chip that used to sit beside the plan name. The chip
 * competed with the name for a row that is already the card's loudest line,
 * and it clipped "Diamante" on a phone; the badge says the same thing without
 * taking width from anything.
 *
 * @param props - The card's availability, recommendation and selection state.
 * @returns The badge, or null when the card has nothing to announce.
 */
const PlanBadge: React.FC<{
  status: SpotsStatus; available: boolean; recommended: boolean; selected: boolean;
}> = ({ status, available, recommended, selected }) => {
  const { t } = useTranslation('@pages/onboarding');
  // A plan that never opened and one that filled up both read as unbuyable, and
  // the card treats them alike; only the ribbon tells them apart, because only
  // one of the two sentences is true of any given plan.
  const unavailable = status === 'coming_soon' ? 'comingSoon' : 'soldOut';
  const state = !available ? unavailable : selected ? 'selected' : recommended ? 'recommended' : null;
  if (!state) return null;
  return (
    <Text
      component="span"
      style={{
        position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)',
        fontSize: 'var(--text-micro)', fontWeight: 'var(--font-bold)',
        letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap',
        padding: '4px 11px', borderRadius: 'var(--radius-full)',
        background: available ? 'var(--seed-base)' : 'var(--surface-card)',
        color: available ? 'var(--surface-primary)' : 'var(--text-tertiary-soft)',
        boxShadow: available ? 'var(--shadow-sm)' : 'none',
        zIndex: 1,
      }}
    >
      {t(`planChoice.badge.${state}`)}
    </Text>
  );
};


/**
 * Price per seat, the cycle note, and the chip that says what is being billed.
 *
 * The headline is always what one user costs for one month — the plan total is
 * not comparable across the three cards, and a seat is. Where the catalogue
 * states a seat price it is used as given; where it still prices a package,
 * `perSeatPrice` recovers the seat from the bundle.
 *
 * @param props - The plan, the active cycle, and the card tone.
 * @returns The price block.
 */
const PlanPrice: React.FC<{
  plan: PlanWithFrequency; billingCycle: BillingCycle; tone: CardTone; compact?: boolean;
}> = ({ plan, billingCycle, tone, compact = false }) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  const money = (v: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v);

  const monthly = plan.monthlyPrice ?? plan.price;
  const yearly = plan.yearlyPrice;
  const isYearly = billingCycle === 'yearly' && yearly != null;
  const perMonth = isYearly ? (yearly as number) / MONTHS_PER_YEAR : monthly;
  const seats = plan.users;
  const pricesBySeat = plan.pricePerSeat != null;
  const seatPrice = perSeatPrice(perMonth, seats, pricesBySeat);
  const perSeat = (seats ?? 1) > 1;

  // One line under the price: the headline is always the yearly rate, so the
  // note is what makes the monthly rate visible too, for comparison.
  const note = isYearly
    ? t('planChoice.agency.rowNote', { monthly: money(perSeatPrice(monthly, seats, pricesBySeat)) })
    : perSeat
      ? t('planChoice.note.monthlyAgency', { seats: seats ?? 0 })
      : t('planChoice.note.monthlySolo');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-1-5)', flexWrap: 'wrap' }}>
        <Text
          component="span"
          style={{
            fontFamily: 'var(--font-family-alt-2)',
            fontSize: compact ? 'var(--text-2xl)' : 'var(--text-3xl)',
            fontWeight: 'var(--font-bold)', lineHeight: 1,
            letterSpacing: '-0.02em', color: tone.strong,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {money(seatPrice)}
        </Text>
        <Text
          component="span"
          style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)' }}
        >
          {t(perSeat ? 'planChoice.perSeatMonth' : 'planChoice.perMonth')}
        </Text>
      </div>
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-tag)', color: 'var(--text-tertiary)',
          // Reserved whether or not the line has content, so the CTA below it
          // sits on the same baseline across all three cards.
          minHeight: 16,
        }}
      >
        {note}
      </Text>
    </div>
  );
};

/**
 * The metered rows, stated against the entry plan instead of in absolutes.
 *
 * The caption above them is the part that earns its line: it is what tells the
 * customer the quota is per seat *and* that it comes back every cycle, which
 * is the single most-asked question about a metered plan.
 *
 * @param props - The plan's quota labels and the card tone.
 * @returns The limits block, or null when the plan reports no quota.
/**
 * Prints what a row is worth to this plan, in the value column.
 *
 * A row the plan lacks prints an em dash rather than nothing: the column has
 * to line up across the three cards for the comparison to be readable, and a
 * blank cell reads as a rendering fault.
 *
 * @param value - The resolved row value.
 * @param t - The onboarding translator.
 * @returns The printable value.
 */
export function rowValueText(value: RowValue, t: TFunction): string {
  if (value.kind === 'absent') return EM_DASH;
  if (value.kind === 'included') return t('planChoice.quotaValue.included');
  if (value.kind === 'unlimited') return t('planChoice.quotaValue.unlimited');
  if (value.kind === 'multiplier') {
    return t('planChoice.quotaValue.multiplier', { factor: value.factor });
  }
  return value.text;
}

const PlanFeatures: React.FC<{ rows: ResolvedFeatureRow[]; compact?: boolean }> = ({
  rows, compact = false,
}) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    // Plain rows rather than `List`: the three columns have to be the grid's
    // own children for the track sizes to apply, and `List.Item` wraps whatever
    // it is given — which collapsed the label into an 18px column and broke it
    // one word per line.
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 'var(--space-2)' : 'var(--space-3)' }}>
      {rows.map((row) => (
        <div
          key={row.id}
          style={{
            display: 'grid',
            gridTemplateColumns: '18px minmax(0, 1fr) auto',
            gap: 'var(--space-2-5)',
            alignItems: 'start',
            fontSize: compact ? 'var(--text-tag)' : 'var(--text-small)',
            lineHeight: 1.35,
          }}
        >
          <FeatureMark on={row.on} size={compact ? 14 : 16} />
          <Text
            component="span"
            style={{
              fontSize: compact ? 'var(--text-tag)' : 'var(--text-small)',
              fontWeight: 'var(--font-regular)',
              color: row.on ? 'var(--text-primary)' : 'var(--plan-feature-off)',
              lineHeight: 1.35, textWrap: 'pretty',
            }}
          >
            {t(`planChoice.matrix.${row.id}`)}
          </Text>
          <Text
            component="span"
            style={{
              fontSize: 'var(--text-tag)',
              fontWeight: 'var(--font-semibold)',
              color: row.on ? 'var(--seed-base)' : 'var(--plan-feature-off)',
              whiteSpace: 'nowrap',
              lineHeight: 1.35,
              paddingTop: 1,
            }}
          >
            {rowValueText(row.value, t)}
          </Text>
        </div>
      ))}
    </div>
  );
};

interface PlanCardProps {
  plan: PlanWithFrequency;
  billingCycle: BillingCycle;
  selected: boolean;
  onSelect: (plan: PlanWithFrequency) => void;
  /** Registers waitlist interest when the plan is sold out. */
  onSoldOutAction?: (plan: PlanWithFrequency) => void;
  /** Whether the authenticated user already joined this plan's waitlist. */
  isWaitlisted?: boolean;
  ctaLabel: string;
  /** This plan's quota wording, built across the catalogue by `buildQuotaLabels`. */
  quotaLabels?: Partial<Record<QuotaKey, QuotaLabel>>;
  compact?: boolean;
  fillHeight?: boolean;
  /** How many rows to draw before the rest are folded away. */
  featureLimit?: number;
  /** Whether the fold is currently open. Shared across the cards. */
  featuresExpanded?: boolean;
  /** Opens or closes the fold on every card at once. */
  onToggleFeatures?: () => void;
  /** Whether the "free days" badge may show: false once the owner has had a subscription. */
  trialOffered?: boolean;
}

/**
 * Builds the card frame for the plan's availability, recommendation and
 * selection state.
 *
 * @param available - Whether the plan can be chosen.
 * @param recommended - Whether it is the recommended tier.
 * @param selected - Whether it is the chosen one.
 * @returns Background, border, and shadow for the card shell.
 */
function cardFrame(available: boolean, recommended: boolean, selected: boolean) {
  // --surface-raised, not --surface-primary: the card stands *on* the page
  // ground, and painting it the ground's own colour left three panels with no
  // edge against the background they sit on.
  if (!available) {
    return { background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', boxShadow: 'none' };
  }
  if (selected) {
    return {
      background: 'var(--surface-raised)',
      border: '2px solid var(--seed-base)',
      boxShadow: 'var(--shadow-lg)',
    };
  }
  return {
    background: 'var(--surface-raised)',
    border: `1px solid ${recommended ? 'var(--seed-base)' : 'var(--border-subtle)'}`,
    boxShadow: recommended ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
  };
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  billingCycle,
  selected,
  onSelect,
  onSoldOutAction,
  isWaitlisted = false,
  ctaLabel,
  quotaLabels = {},
  compact = false,
  fillHeight = false,
  featureLimit,
  featuresExpanded = false,
  onToggleFeatures,
  trialOffered = true,
}) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  // Availability is whether the plan can still be bought, which is what
  // `availableSpots` reports. It used to read `plan.recommended`, so the two
  // tiers without the badge were rendered unclickable.
  const available = isPlanSelectable(plan);
  const status = getSpotsStatus(plan);
  const tier = resolveTier(plan.key);
  const accent = TIER_ACCENT[tier];
  const TierIcon = planIconFor(plan.key);
  const tone: CardTone = {
    accent,
    iconAccent: available ? accent : 'var(--border-medium)',
    strong: available ? 'var(--text-primary)' : 'var(--text-secondary)',
  };

  // Storage is a row now, so the matrix needs the plan's limits and its place
  // on the quota ladder, not just its feature toggles.
  const rows = featureMatrix(
    { features: enabledFeatures(plan.features), limits: plan.limits, quotas: quotaLabels },
    {
      storage: (gigabytes) => formatStorage(gigabytes, (n) => n.toLocaleString(i18n.language)),
    },
  );
  const perSeat = (plan.users ?? 1) > 1;
  const audience = stripSeparatorDash(
    plan.description || t(`planChoice.audiences.${tier}`, { defaultValue: '' }),
  );

  const handleActivate = () => {
    if (available) onSelect(plan);
  };

  const handleCta = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (available) {
      onSelect(plan);
      return;
    }
    if (!isWaitlisted) onSoldOutAction?.(plan);
  };

  return (
    <div
      data-testid={`glass-card-${plan.key}`}
      data-selected={selected ? 'true' : 'false'}
      data-waitlisted={isWaitlisted ? 'true' : 'false'}
      onClick={handleActivate}
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', borderRadius: 'var(--radius-xl)',
        minWidth: 0,
        // The badge hangs 9px over the top edge, so the card cannot clip its
        // own overflow the way it used to.
        flex: fillHeight ? '1 0 auto' : '1 1 0',
        cursor: available ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        ...cardFrame(available, Boolean(plan.recommended) && !selected, selected),
        ...(fillHeight && !compact ? { minHeight: '100%', boxSizing: 'border-box' as const } : {}),
      }}
    >
      <PlanBadge
        status={status}
        available={available}
        recommended={Boolean(plan.recommended)}
        selected={selected}
      />
      <div
        style={{
          display: 'flex', flexDirection: 'column', flex: 1,
          gap: compact ? 'var(--space-3)' : 'var(--space-5)',
          padding: compact
            ? 'var(--space-4) var(--space-3) var(--space-3)'
            : 'var(--space-7) var(--space-6) var(--space-6)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)' }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2-5)',
              minWidth: 0,
              // The name never gives up width to the free-days pill: a plan
              // called "Diamante" wrapping to "Diamant…" is the regression
              // the old status chip caused on a phone. The row wraps instead,
              // and the pill is what goes to the second line.
              flexWrap: 'wrap', rowGap: 'var(--space-1-5)',
            }}
          >
            <Text
              component="span"
              style={{
                width: 34, height: 34, flex: 'none', borderRadius: 'var(--radius-lg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: available ? TIER_TILE[tier] : 'var(--surface-card)',
                color: tone.iconAccent,
              }}
            >
              <TierIcon width={22} height={22} style={{ flex: 'none' }} />
            </Text>
            <Text
              component="span"
              style={{
                fontFamily: 'var(--font-family-alt-2)',
                fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)',
                color: available ? 'var(--text-primary)' : 'var(--text-tertiary-soft)',
                letterSpacing: '-0.01em', lineHeight: 1.1,
                whiteSpace: 'nowrap', minWidth: 0,
              }}
            >
              {plan.title}
            </Text>
            {available && trialOffered && <PlanTrialNote plan={plan} billingCycle={billingCycle} />}
          </div>
          {audience && !compact && (
            <Text
              component="span"
              style={{
                fontSize: 'var(--text-small)', color: 'var(--text-secondary)',
                lineHeight: 1.45, minHeight: AUDIENCE_MIN_HEIGHT, textWrap: 'pretty',
              }}
            >
              {audience}
            </Text>
          )}
        </div>

        <PlanPrice plan={plan} billingCycle={billingCycle} tone={tone} compact={compact} />

        <PlanCardCta
          disabled={!available}
          isWaitlisted={isWaitlisted}
          selected={selected}
          ctaLabel={ctaLabel}
          soldOutCta={isWaitlisted
            ? t(`planChoice.waitlist.willBeNotified_${status}`, {
              defaultValue: t('planChoice.waitlist.willBeNotified'),
            })
            : t(compact ? 'planChoice.enterWaitlistShort' : 'planChoice.enterWaitlist')}
          selectedLabel={t('planChoice.selected')}
          onClick={handleCta}
        />

        <div style={{ height: 1, background: 'var(--border-ultra-subtle)' }} />

        <PlanFeatures
          rows={featureLimit && !featuresExpanded ? rows.slice(0, featureLimit) : rows}
          compact={compact}
        />

        {/* Only when there is something folded away. On a phone the matrix is
            most of the card's height, and three cards of it is a scroll the
            buyer has to remember across rather than a comparison they can
            make. stopPropagation because the whole card is the CTA. */}
        {onToggleFeatures && featureLimit != null && rows.length > featureLimit && (
          <UnstyledButton
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onToggleFeatures();
            }}
            data-testid={`plan-card-features-${plan.key}`}
            style={{
              alignSelf: 'flex-start', background: 'none', cursor: 'pointer',
              fontSize: 'var(--text-tag)', fontWeight: 'var(--font-semibold)',
              color: 'var(--link-text)',
            }}
          >
            {featuresExpanded
              ? t('planChoice.features.showLess')
              : t('planChoice.features.showAll', { count: rows.length })}
          </UnstyledButton>
        )}
      </div>
    </div>
  );
};
